import { IGiveItem, IItem } from '../../../../../../api/types/item'
import { IGame } from '../../../../../../api/types/game'
import React from 'react'
import { ActionIconButton } from '../../../../../../components/ActionIconButton/ActionIconButton'
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material'
import PlaylistAddCircleIcon from '@mui/icons-material/PlaylistAddCircle'
import { Controller, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mutationGiveItem } from '../../../../../../api/mutations'
import { characterInventoryQueryKey, gameCharsQueryKey } from '../../../../../../api/queries'
import { useApiErrors } from '../../../../../../hooks/useApiErrors'
import { DialogActions, DialogContent, Modal } from '../../../../../../components/Modal/Modal'
import { useShow } from '../../../../../../hooks/useShow'
import { FormRow } from '../../../../../../components/FormRow/FormRow'
import { ErrorExplanation } from '../../../../../../components/ErrorExplanation/ErrorExplanation'
import { SubmitButton } from '../../../../../../components/SubmitButton/SubmitButton'
import { Item } from '../../../../../ItemsIndex/components/Item/Item'
import { ICharacterItem, IGameCharacter } from '../../../../../../api/types/character'
import { queryGameCharacters } from '../../../../../../api/queries'
import { AxiosResponse } from 'axios'

interface Props {
  item: IItem
  game: IGame
}

export const GiveItemModal: React.FC<Props> = ({ item, game }) => {
  const { show, doHide, doShow } = useShow()
  const { apiErrors, setApiErrors, setApiErrorsFromAxios } = useApiErrors()
  const queryClient = useQueryClient()
  const { register, handleSubmit, errors, control } = useForm<IGiveItem>()

  const { isLoading, data: chars } = useQuery<IGameCharacter[]>(
    gameCharsQueryKey(game.id),
    () => queryGameCharacters(game.slug),
    {
      onError: (err: any) => {
        setApiErrorsFromAxios(err, 'Nie udało się pobrać listy postaci')
      },
    }
  )

  const { mutate: executeMutation, isLoading: isMutating } = useMutation(
    (give: IGiveItem) => mutationGiveItem(give),
    {
      onError: (err: any) => {
        setApiErrorsFromAxios(err, 'Nie udało się dać przedmiotu')
      },
      onSuccess: async (resp: AxiosResponse<ICharacterItem>) => {
        setApiErrors([])
        queryClient
          .invalidateQueries(characterInventoryQueryKey(resp?.data?.character_id))
          .then(doHide)
      },
    }
  )
  const onSubmit = (data: IGiveItem) => executeMutation(data)

  return (
    <>
      <ActionIconButton disabled={false} onClick={doShow}>
        <Tooltip title="Daj postaci">
          <PlaylistAddCircleIcon />
        </Tooltip>
      </ActionIconButton>
      <Modal maxWidth={'lg'} open={show} onClose={doHide} title={'Daj przedmiot'}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Box sx={{ marginBottom: '16px' }}>
              <Item maxWidth={1000} item={item} showActions={false} reverseBackground={true} />
            </Box>
            <input type="hidden" name="game_id" value={game.id} ref={register()} />
            <input type="hidden" name="item_id" value={item.id} ref={register()} />
            <FormRow>
              <ErrorExplanation errors={apiErrors} />
            </FormRow>

            <FormRow>
              <Controller
                control={control}
                name="character_id"
                defaultValue={''}
                rules={{ required: true }}
                error={!!errors.character_id}
                helperText={errors.character_id && 'Wymagane'}
                render={({ onChange, value, name, ref }) => (
                  <>
                    <TextField
                      select
                      fullWidth={true}
                      name="character_id"
                      defaultValue={''}
                      label="Postać"
                      variant="outlined"
                      inputRef={ref}
                      onChange={val => onChange(val)}
                      error={!!errors.character_id}
                      helperText={errors.character_id && 'Wymagane'}
                      InputProps={{
                        startAdornment: (
                          <>
                            {isLoading && (
                              <InputAdornment position="start">
                                <CircularProgress size={20} />
                              </InputAdornment>
                            )}
                          </>
                        ),
                      }}
                    >
                      {chars &&
                        chars.map(char => (
                          <MenuItem key={char.character.id} value={char.character.id}>
                            {char.character.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </>
                )}
              />
            </FormRow>

            <FormRow>
              <TextField
                name="quantity"
                label="Ilość (tylko dla stackowalnych)"
                type={'number'}
                fullWidth={true}
                disabled={!item.stackable}
                variant="outlined"
                inputProps={{
                  ref: register(),
                  min: 1,
                }}
              />
            </FormRow>
            <FormRow>
              <FormControlLabel
                control={<Checkbox color="primary" defaultChecked={false} />}
                label="Daj jako zidentyfikowany"
                labelPlacement="end"
                name="identified"
                inputRef={register}
              />
            </FormRow>
          </DialogContent>

          <DialogActions>
            <SubmitButton disabled={isMutating}>Dej mu, bo ma chorom curke</SubmitButton>
          </DialogActions>
        </form>
      </Modal>
    </>
  )
}
